import React from "react";
import THEME from "./theme";
import SPECIES_DATA from "./data/species";
import CANDIDATE_SITES, { DATA_SOURCES } from "./data/sites";
import { HABITAT_LOSS_PROJECTIONS, ML_ARCHITECTURE_LAYERS } from "./data/migration";
import { GlowDot, Badge, RiskBadge, ThermalBar } from "./components/UIComponents";
import WestCoastMap from "./components/WestCoastMap";
import PlumeThermalSection from "./components/PlumeThermalSection";
import DocumentManager from "./components/DocumentManager";
import LiveDataPanel from "./components/LiveDataPanel";
import ApiSectionErrorBoundary from "./components/ApiSectionErrorBoundary";
import ServiceErrorBoundary from "./components/ServiceErrorBoundary";
import MigrationTimeline from "./components/MigrationTimeline";

export default function DataWorkspace({
  sub,
  selectedSite,
  setSelectedSite,
  selectedSpecies,
  setSelectedSpecies,
  plume,
  updatePlume,
}) {
  if (sub === "sites") {
    return (
      <div className="animate-ds-fade-in grid min-h-[60vh] gap-4 lg:grid-cols-[280px_1fr]">
        <div className="min-h-0 overflow-auto rounded-ds-xl border border-border bg-surface p-2 shadow-card">
          <WestCoastMap sites={CANDIDATE_SITES} selectedSite={selectedSite} onSelectSite={setSelectedSite} />
        </div>
        <div className="flex min-h-0 flex-col gap-4 overflow-auto">
          {selectedSite && (
            <>
              <div className="card p-4">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-fg">{selectedSite.name}</h2>
                    <p className="text-xs text-fg-secondary">
                      {selectedSite.region} · {selectedSite.lat.toFixed(2)}°N, {Math.abs(selectedSite.lng).toFixed(2)}°W
                    </p>
                  </div>
                  <RiskBadge level={selectedSite.riskLevel} />
                </div>
                <div className="mb-3 grid grid-cols-2 gap-3">
                  <div className="rounded-ds border border-border bg-canvas-subtle p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Summer SST</div>
                    <div className="text-lg font-bold text-warning">{selectedSite.summerSST}</div>
                  </div>
                  <div className="rounded-ds border border-border bg-canvas-subtle p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Winter SST</div>
                    <div className="text-lg font-bold text-accent">{selectedSite.winterSST}</div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Key species</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedSite.keySpecies.map((s) => (
                      <Badge key={s} color={THEME.textMuted}>
                        {s}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Vulnerabilities</div>
                  {selectedSite.vulnerabilities.map((v, i) => (
                    <div key={i} className="mb-1 flex items-center gap-2 text-xs text-fg">
                      <GlowDot color={THEME.danger} size={4} /> {v}
                    </div>
                  ))}
                </div>
                <div className="rounded-ds border border-danger/20 bg-danger/5 p-3">
                  <div className="mb-1 text-[10px] font-bold text-danger">Regulatory note</div>
                  <p className="text-xs leading-relaxed text-fg-secondary">{selectedSite.regulatoryNote}</p>
                </div>
              </div>
              <div className="card p-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-accent">Connected data feeds</div>
                {DATA_SOURCES.map((ds, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-border py-2 last:border-0">
                    <div>
                      <div className="text-xs font-semibold text-fg">{ds.name}</div>
                      <div className="text-[10px] text-fg-tertiary">{ds.dataset}</div>
                    </div>
                    <GlowDot color={ds.status === "LIVE" ? THEME.success : THEME.accent} size={5} pulse={ds.status === "LIVE"} />
                  </div>
                ))}
              </div>
              <ApiSectionErrorBoundary>
                <LiveDataPanel lat={selectedSite.lat} lng={selectedSite.lng} siteName={selectedSite.name} pollMinutes={15} />
              </ApiSectionErrorBoundary>
            </>
          )}
        </div>
      </div>
    );
  }

  if (sub === "species") {
    return (
      <div className="animate-ds-fade-in grid gap-4 lg:grid-cols-[240px_1fr]">
        <div className="flex flex-col gap-1">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Focal species</p>
          {SPECIES_DATA.map((sp) => (
            <button
              key={sp.id}
              type="button"
              onClick={() => setSelectedSpecies(sp)}
              className={`flex items-center gap-2 rounded-ds border px-3 py-2 text-left text-xs transition-colors duration-fast md:text-sm ${
                selectedSpecies.id === sp.id ? "border-accent bg-accent-muted font-semibold text-fg" : "border-border bg-surface hover:bg-canvas-subtle"
              }`}
            >
              <span className="text-lg" aria-hidden>
                {sp.icon}
              </span>
              <span>
                <span className="block font-medium">{sp.name}</span>
                <span className="block text-[10px] italic text-fg-tertiary">{sp.scientific}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-4">
          <div className="card p-5">
            <div className="mb-4 flex flex-wrap justify-between gap-4">
              <div className="flex items-start gap-2">
                <span className="text-3xl" aria-hidden>
                  {selectedSpecies.icon}
                </span>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: selectedSpecies.color }}>
                    {selectedSpecies.name}
                  </h2>
                  <p className="text-xs italic text-fg-secondary">{selectedSpecies.scientific}</p>
                  <p className="mt-1 text-xs text-fg-secondary">{selectedSpecies.status}</p>
                </div>
              </div>
              {selectedSpecies.lethalTemp && (
                <div className="text-right">
                  <div className="text-[10px] font-bold text-danger">Lethal</div>
                  <div className="font-mono text-2xl font-bold text-danger">{selectedSpecies.lethalTemp}°C</div>
                </div>
              )}
            </div>
            <div className="mb-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Thermal tolerance (°C)</div>
              <ThermalBar thresholds={selectedSpecies.thresholds} ambientTemp={12} />
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedSpecies.thresholds.map((t, i) => (
                  <div key={i} className="flex items-center gap-1 text-[10px] text-fg-secondary">
                    <span className="h-2 w-2 rounded-sm" style={{ background: `${t.color}99` }} aria-hidden />
                    {t.label}
                    {t.max != null ? ` (${t.max}°C)` : ""}
                  </div>
                ))}
                <div className="flex items-center gap-1 text-[10px] text-accent">
                  <span className="h-0.5 w-2 bg-accent" aria-hidden />
                  Ambient SST marker (12°C)
                </div>
              </div>
            </div>
            <div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Key findings</div>
              {selectedSpecies.keyFacts.map((f, i) => (
                <div key={i} className="mb-2 flex gap-2 text-sm text-fg-secondary">
                  <GlowDot color={selectedSpecies.color} size={5} />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card p-4">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Comparative vulnerability</div>
            {SPECIES_DATA.filter((s) => s.lethalTemp).map((sp) => (
              <div key={sp.id} className="mb-2 grid grid-cols-[110px_1fr_52px] items-center gap-2">
                <span className="text-xs font-semibold" style={{ color: sp.color }}>
                  {sp.icon} {sp.name.split(" ")[0]}
                </span>
                <div className="relative h-4 overflow-hidden rounded bg-canvas-muted">
                  <div className="absolute bottom-0 top-0 rounded-sm" style={{ left: `${(sp.optimalRange[0] / 32) * 100}%`, width: `${((sp.optimalRange[1] - sp.optimalRange[0]) / 32) * 100}%`, background: `${sp.color}55` }} />
                  <div className="absolute bottom-0 top-0 w-0.5 bg-danger" style={{ left: `${(sp.lethalTemp / 32) * 100}%` }} />
                </div>
                <span className="text-right font-mono text-[10px] text-danger">☠ {sp.lethalTemp}°C</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (sub === "plume") {
    return (
      <div className="animate-ds-fade-in grid gap-4 lg:grid-cols-[260px_1fr]">
        <div className="card h-fit p-4">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-accent">Parameters</div>
          {[
            { key: "powerMW", label: "Power (MW)", min: 0.1, max: 50, step: 0.1, unit: "MW" },
            { key: "dischargeTemp", label: "ΔT discharge (°C)", min: 0.5, max: 15, step: 0.5, unit: "°C" },
            { key: "currentSpeed", label: "Current (m/s)", min: 0.01, max: 1, step: 0.01, unit: "m/s" },
            { key: "ambientTemp", label: "Ambient (°C)", min: 5, max: 22, step: 0.5, unit: "°C" },
            { key: "depth", label: "Depth (m)", min: 5, max: 200, step: 5, unit: "m" },
          ].map((p) => (
            <div key={p.key} className="mb-4">
              <div className="mb-1 flex justify-between text-[10px] text-fg-secondary">
                <label htmlFor={`rng-${p.key}`}>{p.label}</label>
                <span className="font-mono text-xs font-semibold text-fg">
                  {plume[p.key]} {p.unit}
                </span>
              </div>
              <input
                id={`rng-${p.key}`}
                type="range"
                min={p.min}
                max={p.max}
                step={p.step}
                value={plume[p.key]}
                onChange={(e) => updatePlume(p.key, parseFloat(e.target.value))}
              />
            </div>
          ))}
          <div className="mt-2 rounded-ds border border-border bg-canvas-subtle p-3">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Regulatory limits shown</div>
            {[
              { color: THEME.success, label: "0.3°C — EPA/WA/OR incremental" },
              { color: THEME.accent, label: "±1°C — CCME marine" },
              { color: THEME.warning, label: "4°F (2.2°C) — CA Thermal Plan" },
            ].map((r, i) => (
              <div key={i} className="mb-1 flex items-center gap-2 text-[10px]" style={{ color: r.color }}>
                <span className="h-0.5 w-3 rounded-full" style={{ background: r.color }} aria-hidden />
                {r.label}
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-ds border border-danger/20 bg-danger/5 p-3">
            <div className="mb-1 text-[10px] font-bold text-danger">Note</div>
            <p className="text-[10px] leading-relaxed text-fg-secondary">
              2D advection-diffusion screening—suitable for screening, not permit-grade hydrodynamic certification.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <PlumeThermalSection plume={plume} />
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { label: "Heat output", value: `${(plume.powerMW * 0.9).toFixed(1)} MW`, sub: "90% thermal", color: THEME.danger },
              {
                label: "Regulatory",
                value: plume.dischargeTemp > 2.2 ? "Exceeds CA" : plume.dischargeTemp > 1 ? "Exceeds CCME" : plume.dischargeTemp > 0.3 ? "Exceeds EPA" : "Compliant",
                sub: "At mixing zone edge",
                color: plume.dischargeTemp > 1 ? THEME.danger : plume.dischargeTemp > 0.3 ? THEME.warning : THEME.success,
              },
              {
                label: "Species at risk",
                value: `${SPECIES_DATA.filter((s) => s.stressOnset && plume.ambientTemp + plume.dischargeTemp >= s.stressOnset).length} of ${SPECIES_DATA.filter((s) => s.stressOnset).length}`,
                sub: "Within stress threshold",
                color: THEME.warning,
              },
            ].map((s, i) => (
              <div key={i} className="card p-3 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">{s.label}</div>
                <div className="font-mono text-lg font-bold" style={{ color: s.color }}>
                  {s.value}
                </div>
                <div className="text-[10px] text-fg-tertiary">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (sub === "migration") {
    return (
      <div className="animate-ds-fade-in flex flex-col gap-4">
        <div className="card p-5">
          <h2 className="mb-1 text-lg font-bold text-accent">Pacific Salmon Migration Timing &amp; Climate-Driven Shifts</h2>
          <p className="mb-4 text-sm text-fg-secondary">
            Adult salmon migration timing across all species, with documented shifts in days per decade. Pink/chum show fastest changes (7 days earlier/decade).
          </p>
          <MigrationTimeline />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="card p-4">
            <div className="mb-3 text-xs font-semibold uppercase text-danger">Habitat loss (medium emissions)</div>
            {HABITAT_LOSS_PROJECTIONS.map((h) => (
              <div key={h.species} className="mb-2">
                <div className="mb-1 flex justify-between text-xs">
                  <span style={{ color: h.color }} className="font-semibold">
                    {h.species}
                  </span>
                  <span className="font-mono text-danger">-{h.loss}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-canvas-muted">
                  <div className="h-full rounded-full" style={{ width: `${h.loss}%`, background: `linear-gradient(90deg, ${h.color}88, ${THEME.danger})` }} />
                </div>
              </div>
            ))}
            <p className="mt-2 text-[10px] text-fg-tertiary">Source: Abdul-Aziz et al. 2011, CJFAS.</p>
          </div>
          <div className="card p-4">
            <div className="mb-3 text-xs font-semibold uppercase text-accent">ML architecture (roadmap)</div>
            {ML_ARCHITECTURE_LAYERS.map((item, i) => (
              <div key={i} className="mb-2 flex items-center justify-between rounded-ds border border-border bg-canvas-subtle px-3 py-2">
                <div>
                  <div className="text-xs font-semibold text-fg">{item.layer}</div>
                  <div className="text-[10px] text-fg-tertiary">{item.tools}</div>
                </div>
                <Badge color={item.status === "Active" ? THEME.success : item.status === "Prototype" ? THEME.accent : THEME.textMuted}>{item.status}</Badge>
              </div>
            ))}
            <p className="mt-3 text-[10px] leading-relaxed text-fg-secondary">
              Architecture: TypeScript frontend → Rust API (Axum) → Python ML services (FastAPI) → TimescaleDB + pgvector.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (sub === "documents") {
    return (
      <div className="animate-ds-fade-in">
        <ServiceErrorBoundary>
          <DocumentManager />
        </ServiceErrorBoundary>
      </div>
    );
  }

  return null;
}
